import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Card, CardContent, Divider, FormControlLabel,
  MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RouteIcon from '@mui/icons-material/Route';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TuneIcon from '@mui/icons-material/Tune';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';

import { fetchRequest as updateGoal, clearState as clearUpdate } from '../../redux/actions/discovery/updateGoal';
import { fetchRequest as fetchModels } from '../../redux/actions/discovery/models';
import { fetchRequest as fetchBlueprints } from '../../redux/actions/blueprints/list';
import {
  CADENCES, FREQUENCIES, DISCOVERY_MODES, RETRAIN_MODES, SERVING_MODES,
  AUTOPILOT_MODES, DEPLOY_MODES, FLEET_RETRAIN_MODES, FLEET_SERVING_MODES,
  FLEET_DEPLOY_MODES, normalizePolicyMode, isFleetGoalType,
  BUDGET_LABEL, HORIZON_MIN, HORIZON_MAX, LAG_MIN, LAG_MAX,
  goalCopy, fmtSpan, SpanPicker, ModelLineupPicker,
} from './goalControls';

const helpFor = (list, value) => (list.find((o) => o.value === value) || {}).help;

/**
 * Per-goal settings tab (spec §5.4). Mirrors the goal-creation wizard's
 * option set — tuning (cadence / horizon / lookback), retrain policy,
 * autopilot, after-discovery pipeline and the full model lineup (public +
 * your own designs) — plus the live-monitoring and multi-hop controls that
 * only make sense once a goal exists. Every change saves immediately through
 * PATCH /goals/:id; the create wizard and this tab share one source
 * of truth for the controls (./goalControls).
 */
const GoalSettingsCard = ({ goal, embedded = false }) => {
  const dispatch = useDispatch();
  const updateState = useSelector((s) => s.discovery_update_goal);
  const models = useSelector((s) => s.discovery_models?.response?.models) || [];
  const modelsLoading = useSelector((s) => Boolean(s.discovery_models?.isLoading));
  const blueprints = useSelector((s) => s.blueprints_list?.response?.models) || [];
  const blueprintsLoading = useSelector((s) => Boolean(s.blueprints_list?.isLoading));

  const options = goal?.options || {};
  const policy = options.retrain_policy || {};
  const pipeline = options.pipeline || {};

  const [name, setName] = useState(goal?.name || '');
  const [live, setLive] = useState(options.live !== false);
  const [mode, setMode] = useState(normalizePolicyMode(policy.mode));
  const [frequency, setFrequency] = useState(policy.frequency_seconds || 7 * 86400);
  // Discovery + serving policies (auto | scheduled | manual; auto default).
  const [discoveryMode, setDiscoveryMode] = useState(
    normalizePolicyMode(options.discovery_policy?.mode));
  const [discoveryFreq, setDiscoveryFreq] = useState(
    options.discovery_policy?.frequency_seconds || 7 * 86400);
  const [servingMode, setServingMode] = useState(
    normalizePolicyMode(options.serving_policy?.mode));
  const [servingFreq, setServingFreq] = useState(
    options.serving_policy?.frequency_seconds || 86400);
  const [autopilot, setAutopilot] = useState(options.autopilot || 'full');
  const [spendCap, setSpendCap] = useState(options.monthly_spend_cap || 500);
  const [autoFit, setAutoFit] = useState(pipeline.auto_fit !== false);
  const [autoDeploy, setAutoDeploy] = useState(pipeline.auto_deploy || 'auto');
  const [deselected, setDeselected] = useState(
    () => new Set(options.model_selection?.exclude || []));
  const [bpSelected, setBpSelected] = useState(
    () => new Set(options.model_selection?.user_blueprints || []));

  // Tuning — cadence/horizon/lookback. Persisted on commit; they reshape the
  // next discovery run rather than the current champion.
  const [interval, setIntervalS] = useState(goal?.interval_seconds || 3600);
  const [horizon, setHorizon] = useState(goal?.horizon_bins || 12);
  const [lag, setLag] = useState(options.max_lag_bins || 48);

  useEffect(() => {
    const opts = goal?.options || {};
    setName(goal?.name || '');
    setLive(opts.live !== false);
    setMode(normalizePolicyMode(opts.retrain_policy?.mode));
    setFrequency(opts.retrain_policy?.frequency_seconds || 7 * 86400);
    setDiscoveryMode(normalizePolicyMode(opts.discovery_policy?.mode));
    setDiscoveryFreq(opts.discovery_policy?.frequency_seconds || 7 * 86400);
    setServingMode(normalizePolicyMode(opts.serving_policy?.mode));
    setServingFreq(opts.serving_policy?.frequency_seconds || 86400);
    setAutopilot(opts.autopilot || 'full');
    setSpendCap(opts.monthly_spend_cap || 500);
    const pl = opts.pipeline || {};
    setAutoFit(pl.auto_fit !== false);
    setAutoDeploy(pl.auto_deploy || 'auto');
    setDeselected(new Set(opts.model_selection?.exclude || []));
    setBpSelected(new Set(opts.model_selection?.user_blueprints || []));
    setIntervalS(goal?.interval_seconds || 3600);
    setHorizon(goal?.horizon_bins || 12);
    setLag(opts.max_lag_bins || 48);
  }, [goal]);

  useEffect(() => {
    if (goal?.goal_type) dispatch(fetchModels({ goalType: goal.goal_type }));
  }, [goal?.goal_type, dispatch]);

  useEffect(() => () => { dispatch(clearUpdate()); }, [dispatch]);

  const save = (fields) => dispatch(updateGoal({ goalId: goal.goal_id, fields }));

  // Slider drags fire continuously — coalesce tuning writes so we PATCH once
  // the value settles instead of on every tick.
  const tuneTimers = useRef({});
  const debouncedSave = (key, fields) => {
    if (tuneTimers.current[key]) clearTimeout(tuneTimers.current[key]);
    tuneTimers.current[key] = setTimeout(() => save(fields), 450);
  };
  useEffect(() => () => {
    Object.values(tuneTimers.current).forEach((t) => clearTimeout(t));
  }, []);

  // Commit the rename on blur / Enter. An empty value reverts to the current
  // name instead of saving — the backend ignores blank names anyway.
  const onNameCommit = () => {
    const next = name.trim();
    if (!next) {
      setName(goal?.name || '');
      return;
    }
    if (next !== goal?.name) save({ name: next });
  };

  const savePipeline = (patch) => {
    const next = { auto_fit: autoFit, auto_deploy: autoDeploy, ...patch };
    save({ pipeline: next });
  };

  const onModelsChange = ({ deselected: nextDes, bpSelected: nextBp }) => {
    setDeselected(nextDes);
    setBpSelected(nextBp);
    save({ model_selection: { exclude: [...nextDes], user_blueprints: [...nextBp] } });
  };

  const onLiveToggle = (checked) => {
    setLive(checked);
    save({ live: checked });
  };

  const policyPayload = (value, freq) =>
    (value === 'scheduled' ? { mode: value, frequency_seconds: freq } : { mode: value });

  const onModeChange = (value) => {
    setMode(value);
    save({ retrain_policy: policyPayload(value, frequency) });
  };

  const onFrequencyChange = (value) => {
    setFrequency(value);
    save({ retrain_policy: { mode: 'scheduled', frequency_seconds: value } });
  };

  const onDiscoveryModeChange = (value) => {
    setDiscoveryMode(value);
    save({ discovery_policy: policyPayload(value, discoveryFreq) });
  };

  const onDiscoveryFreqChange = (value) => {
    setDiscoveryFreq(value);
    save({ discovery_policy: { mode: 'scheduled', frequency_seconds: value } });
  };

  const onServingModeChange = (value) => {
    setServingMode(value);
    save({ serving_policy: policyPayload(value, servingFreq) });
  };

  const onServingFreqChange = (value) => {
    setServingFreq(value);
    const fields = { serving_policy: { mode: 'scheduled', frequency_seconds: value } };
    if (isFleetGoalType(goal?.goal_type)) {
      fields.options = {
        ...(goal?.options || {}),
        fleet: { ...(goal?.options?.fleet || {}), run_interval_seconds: Number(value) },
      };
    }
    save(fields);
  };

  const onAutopilotChange = (value) => {
    setAutopilot(value);
    save({ autopilot: value });
  };

  const onSpendCapBlur = () => {
    const cap = Number(spendCap);
    if (Number.isFinite(cap) && cap > 0
        && cap !== (options.monthly_spend_cap || 500)) {
      save({ monthly_spend_cap: cap });
    }
  };

  const onCadenceChange = (value) => {
    setIntervalS(value);
    save({ interval_seconds: value });
  };

  const onHorizonChange = (value) => {
    setHorizon(value);
    debouncedSave('horizon', { horizon_bins: value });
  };

  const onLagChange = (value) => {
    setLag(value);
    debouncedSave('lag', { max_lag_bins: value });
  };

  // Cadence picker — show the goal's interval even if it isn't a standard preset.
  const cadenceOptions = useMemo(() => {
    if (CADENCES.some((c) => c.value === interval)) return CADENCES;
    return [{ value: interval, label: `Every ${fmtSpan(1, interval)}` }, ...CADENCES];
  }, [interval]);

  if (!goal) return null;

  const fleet = isFleetGoalType(goal.goal_type);
  const retrainModes = fleet ? FLEET_RETRAIN_MODES : RETRAIN_MODES;
  const servingModes = fleet ? FLEET_SERVING_MODES : SERVING_MODES;
  const deployModes = fleet ? FLEET_DEPLOY_MODES : DEPLOY_MODES;

  const inner = (
    <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DriveFileRenameOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Name</Typography>
          </Stack>

          <TextField
            size="small"
            label="Goal name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={onNameCommit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            helperText="Shown everywhere this goal appears — the list, breadcrumbs and alerts."
            sx={{ maxWidth: 480 }}
          />

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <MonitorHeartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Live monitoring &amp; retraining</Typography>
          </Stack>

          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={live}
                onChange={(e) => onLiveToggle(e.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2">
                Monitor relationships continuously
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  re-checks every driver on a rolling window and flags weakening ones
                </Typography>
              </Typography>
            )}
          />

          <Divider />

          {/* Automation policies — the three actions share one vocabulary
              (auto | scheduled | manual), mirroring the create wizard. */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>Re-run discovery</Typography>
            <Select
              size="small"
              value={discoveryMode}
              onChange={(e) => onDiscoveryModeChange(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            >
              {DISCOVERY_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
            {discoveryMode === 'scheduled' && (
              <Select
                size="small"
                value={discoveryFreq}
                onChange={(e) => onDiscoveryFreqChange(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                {FREQUENCIES.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {helpFor(DISCOVERY_MODES, discoveryMode)}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>
              {fleet ? 'Re-run the bake-off' : 'Retrain the model'}
            </Typography>
            <Select
              size="small"
              value={mode}
              onChange={(e) => onModeChange(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            >
              {retrainModes.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
            {mode === 'scheduled' && (
              <Select
                size="small"
                value={frequency}
                onChange={(e) => onFrequencyChange(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                {FREQUENCIES.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {helpFor(retrainModes, mode)}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>
              {fleet ? 'Re-score the fleet' : 'Produce forecasts'}
            </Typography>
            <Select
              size="small"
              value={servingMode}
              onChange={(e) => onServingModeChange(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            >
              {servingModes.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
            {servingMode === 'scheduled' && (
              <Select
                size="small"
                value={servingFreq}
                onChange={(e) => onServingFreqChange(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                {CADENCES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {helpFor(servingModes, servingMode)}
          </Typography>

          <Divider />

          {/* Tuning — cadence / horizon / lookback (mirrors the create wizard) */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Tuning</Typography>
          </Stack>

          <TextField
            select
            size="small"
            label="Data cadence"
            value={interval}
            onChange={(e) => onCadenceChange(Number(e.target.value))}
            helperText={fleet
              ? 'How often each member updates — one step equals one interval. Scoring uses this same step.'
              : 'How often your data updates — one step equals one interval.'}
            sx={{ maxWidth: 360 }}
          >
            {cadenceOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ maxWidth: 480 }}>
            <SpanPicker
              icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              title={goalCopy(goal.goal_type).horizonTitle}
              value={horizon}
              min={HORIZON_MIN}
              max={HORIZON_MAX}
              interval={interval}
              overridden={horizon !== (goal?.horizon_bins || 12)}
              onChange={onHorizonChange}
            />
          </Box>

          <Box sx={{ maxWidth: 480 }}>
            <SpanPicker
              icon={<HistoryIcon sx={{ fontSize: 16 }} />}
              title="History to look back on"
              value={lag}
              min={LAG_MIN}
              max={LAG_MAX}
              interval={interval}
              overridden={lag !== (options.max_lag_bins || 48)}
              onChange={onLagChange}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            {fleet
              ? 'Cadence, horizon and lookback reshape scoring and the next bake-off.'
              : 'Cadence, horizon and lookback reshape the goal on its next discovery run.'}
          </Typography>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <SmartToyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Autopilot</Typography>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>Mode</Typography>
            <Select
              size="small"
              value={autopilot}
              onChange={(e) => onAutopilotChange(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            >
              {AUTOPILOT_MODES.map((a) => (
                <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              type="number"
              label={BUDGET_LABEL}
              value={spendCap}
              onChange={(e) => setSpendCap(e.target.value)}
              onBlur={onSpendCapBlur}
              inputProps={{ min: 1 }}
              sx={{ width: 260 }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {helpFor(AUTOPILOT_MODES, autopilot)}
            {' '}When the monthly budget for automated actions is reached, the
            autopilot pauses itself and notifies you — nothing runs away.
          </Typography>

          <Divider />

          {/* After-discovery chain */}
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">After discovery</Typography>
          </Stack>

          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={autoFit}
                onChange={(e) => { setAutoFit(e.target.checked); savePipeline({ auto_fit: e.target.checked }); }}
              />
            )}
            label={(
              <Typography variant="body2">
                {fleet ? 'Auto-pick the best foundation model' : 'Auto-fit the best model'}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {fleet
                    ? 'races the eligible foundation models after discovery and keeps the best ranker'
                    : 'searches the eligible models after each discovery and keeps the strongest'}
                </Typography>
              </Typography>
            )}
          />

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>Deployment</Typography>
            <Select
              size="small"
              value={autoDeploy}
              onChange={(e) => { setAutoDeploy(e.target.value); savePipeline({ auto_deploy: e.target.value }); }}
              sx={{ minWidth: { xs: '100%', sm: 280 } }}
            >
              {deployModes.map((d) => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </Stack>

          <Divider />

          {/* Model lineup — public catalog + your own designs (blueprints) */}
          <ModelLineupPicker
            key={goal.goal_id}
            title="Models to try"
            helper={fleet
              ? 'A fleet only races foundation models — they forecast every member zero-shot and the best ranker wins. Trees and your own designs are not in this race. Changes apply to the next bake-off.'
              : 'We try the models that fit your data and keep the best. Changes apply to the next fit.'}
            models={models}
            modelsLoading={modelsLoading}
            blueprints={fleet ? [] : blueprints}
            blueprintsLoading={fleet ? false : blueprintsLoading}
            deselected={deselected}
            bpSelected={bpSelected}
            onChange={onModelsChange}
            onEnterPick={fleet ? undefined : () => dispatch(fetchBlueprints())}
            showCost={!fleet}
          />

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <RouteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Multi-hop pathways</Typography>
          </Stack>

          <Typography variant="body2">
            Always on — 3 hops deep
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              e.g. war → oil → flight prices — drivers of your drivers
            </Typography>
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Every run screens what drives each validated driver, builds the chains
            (up to 3 levels), and tests whether upstream sources act through the
            middle hop or directly at their longer total lag. Budgets keep the
            cost bounded: the 10 strongest drivers expand at depth 2, the 5
            strongest at depth 3.
          </Typography>

          {updateState?.error && (
            <Alert severity="error" onClose={() => dispatch(clearUpdate())}>
              {String(updateState.error)}
            </Alert>
          )}
    </Stack>
  );

  if (embedded) return inner;
  return (
    <Card variant="outlined">
      <CardContent>{inner}</CardContent>
    </Card>
  );
};

export default GoalSettingsCard;
